import type { AriaButtonOptions } from "@react-aria/button";
import { useButton } from "@react-aria/button";
import type { ElementType, PropsWithChildren, ReactNode } from "react";
import { forwardRef, useRef } from "react";
import { tv } from "tailwind-variants";

import { useUiKitComponents } from "../ui-kit-provider";

const buttonStyles = tv({
  base: "rounded-[4px] outline-none box-border border-0 flex justify-center items-center cursor-pointer transition-all duration-200 text-white font-noto-sans-sc w-fit font-bold",
  variants: {
    size: {
      large: "[font-size:var(--fontSize-mobile-large-button)]",
      medium: "[font-size:var(--fontSize-mobile-medium-button)]",
      small: "[font-size:var(--fontSize-mobile-small-button)]",
      xsmall: "[font-size:var(--fontSize-mobile-xsmall-button)]",
    },
    mdSize: {
      large: "md:[font-size:var(--fontSize-desktop-large-button)]",
      medium: "md:[font-size:var(--fontSize-desktop-medium-button)]",
      small: "md:[font-size:var(--fontSize-desktop-small-button)]",
      xsmall: "md:[font-size:var(--fontSize-desktop-xsmall-button)]",
    },
    dSize: {
      large: "lg:[font-size:var(--fontSize-desktop-large-button)]",
      medium: "lg:[font-size:var(--fontSize-desktop-medium-button)]",
      small: "lg:[font-size:var(--fontSize-desktop-small-button)]",
      xsmall: "lg:[font-size:var(--fontSize-desktop-xsmall-button)]",
    },
    weight: {
      superbold: "[font-weight:var(--fontWeight-mobile-superbold)]",
      bold: "[font-weight:var(--fontWeight-mobile-bold)]",
      semibold: "[font-weight:var(--fontWeight-mobile-semibold)]",
      normal: "[font-weight:var(--fontWeight-mobile-normal)]",
      light: "[font-weight:var(--fontWeight-mobile-light)]",
    },
    mdWeight: {
      superbold: "md:[font-weight:var(--fontWeight-desktop-superbold)]",
      bold: "md:[font-weight:var(--fontWeight-desktop-bold)]",
      semibold: "md:[font-weight:var(--fontWeight-desktop-semibold)]",
      normal: "md:[font-weight:var(--fontWeight-desktop-normal)]",
      light: "md:[font-weight:var(--fontWeight-desktop-light)]",
    },
    dWeight: {
      superbold: "lg:[font-weight:var(--fontWeight-desktop-superbold)]",
      bold: "lg:[font-weight:var(--fontWeight-desktop-bold)]",
      semibold: "lg:[font-weight:var(--fontWeight-desktop-semibold)]",
      normal: "lg:[font-weight:var(--fontWeight-desktop-normal)]",
      light: "lg:[font-weight:var(--fontWeight-desktop-light)]",
    },
    variant: {
      default:
        "bg-[linear-gradient(-90deg,#ff3d00_0%,#8032ff_100%,#ff3d00_100%)] shadow-[0px_0px_13px_transparent,0px_4px_8px_rgba(6,8,14,0.2),inset_0px_-1px_1px_rgba(35,34,49,0.2),inset_0px_1px_1px_rgba(255,255,255,0.3)] hover:bg-left hover:shadow-[0px_0px_13px_#e1616f,0px_4px_8px_rgba(6,8,14,0.2),inset_0px_-1px_1px_rgba(35,34,49,0.2),inset_0px_1px_1px_rgba(255,255,255,0.3)] active:bg-left active:shadow-[0px_0px_13px_#e1616f,0px_4px_8px_rgba(6,8,14,0.2),inset_0px_-1px_1px_rgba(35,34,49,0.2),inset_0px_1px_1px_rgba(255,255,255,0.3)] active:bg-[linear-gradient(0deg,rgba(0,0,0,0.25),rgba(0,0,0,0.25)),linear-gradient(90deg,#ff3d00_0%,#8032ff_100%,#ff3d00_100%)]  hover:bg-[linear-gradient(90deg,#ff3d00_0%,#8032ff_100%,#ff3d00_100%)]  ",
      transparent: "border border-white hover:bg-white hover:text-black",
      accent: "border-1 bg-white text-black ",
      secondary: "bg-[#28282D]",
    },
    isActive: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: "secondary",
      isActive: true,
      class: "border-[#C8A0F0] border",
    },
  ],
  defaultVariants: {
    variant: "default",
    isActive: false,
  },
});

export type ButtonProps<Element extends ElementType> = PropsWithChildren<
  Omit<
    AriaButtonOptions<Element> &
      UiKitFC<{
        className?: string;
        variant?: "default" | "transparent" | "accent" | "secondary";
        href?: string;
        size?: "large" | "medium" | "small" | "xsmall";
        weight?: "superbold" | "bold" | "semibold" | "normal" | "light";
        onClick?: () => void;
        isActive?: boolean;
      }>,
    "onClick"
  >
>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps<ElementType>>(
  function (
    {
      className,
      variant = "default",
      children,
      href,
      size = "medium",
      weight = "normal",
      d,
      md,
      isActive = false,
      ...props
    },
    ref,
  ): ReactNode {
    const innerRef = useRef<HTMLButtonElement | null>(null);
    const mergedRef =
      ref && typeof ref === "object" && "current" in ref ? ref : innerRef;

    const { buttonProps } = useButton(props, mergedRef);

    const { Link } = useUiKitComponents();

    if (!href) {
      return (
        <button
          {...buttonProps}
          ref={mergedRef}
          className={buttonStyles({
            variant,
            size,
            isActive,
            mdSize: md?.size ?? size,
            dSize: d?.size ?? md?.size ?? size,
            weight,
            mdWeight: md?.weight ?? weight,
            dWeight: d?.weight ?? md?.weight ?? weight,
            className,
          })}
        >
          {children}
        </button>
      );
    }

    if (href.includes("http")) {
      return (
        <a
          className={buttonStyles({
            variant,
            isActive,
            size,
            mdSize: md?.size ?? size,
            dSize: d?.size ?? md?.size ?? size,
            weight,
            mdWeight: md?.weight ?? weight,
            dWeight: d?.weight ?? md?.weight ?? weight,
            className,
          })}
          href={href}
          target={"_blank"}
        >
          {children}
        </a>
      );
    }

    return (
      <Link
        href={href}
        className={buttonStyles({
          variant,
          size,
          isActive,
          mdSize: md?.size ?? size,
          dSize: d?.size ?? md?.size ?? size,
          weight,
          mdWeight: md?.weight ?? weight,
          dWeight: d?.weight ?? md?.weight ?? weight,
          className,
        })}
      >
        {children}
      </Link>
    );
  },
);
